'use client';
import React, { useState } from 'react';
import { DrupalNode } from 'next-drupal';
import { motion } from 'framer-motion';
import Image from 'next/image';
import NewsBackgroundBanner from '@/components/sections/NewsBackgroundBanner';
import { buildMediaTypeAndSrc } from '@/utils';
import RegisterForm from '@/components/sections/RegisterFrom';
import RegisterSuccessModal from '@/components/sections/RegisterSuccessModal';
import Link from 'next/link';
import { MdChevronRight } from 'react-icons/md';
import { config } from '@/lib/config';
import { resolveLink } from '@/utils';
import dynamic from 'next/dynamic';

const ReactPlayer = dynamic(() => import('react-player'), { ssr: false });

interface DetailScreenProps {
	cardDetails: DrupalNode;
}

const DetailScreen: React.FC<DetailScreenProps> = ({ cardDetails }) => {
	const [showSuccessModal, setShowSuccessModal] = useState<boolean>(false);
	const isRegister = cardDetails?.register;

	const mediaTypeAndSrc = buildMediaTypeAndSrc(cardDetails?.media);
	const isEvent = cardDetails?.resource?.name === 'Event';

	return (
		<>
			<div
				className={`pt-20 bg-mapGray ${!cardDetails?.description?.processed ? 'pb-10' : 'pb-0'}`}
			>
				<NewsBackgroundBanner
					cardDetails={cardDetails}
					mediaTypeAndSrc={mediaTypeAndSrc}
				/>

				{isEvent && cardDetails?.summary?.processed && (
					<div className="py-[120px] bg-[#003350] relative mobileMax:py-10 betweenMobileTab:py-16 overflow-hidden">
						<div className="mini-container relative z-[1]">
							<motion.div
								initial={{ opacity: 0, y: 40 }}
								whileInView={{ opacity: 1, y: 0 }}
								viewport={{ once: true }}
								transition={{
									duration: 0,
								}}
								className="remove-animation-fluctuation flex flex-row-reverse gap-[70px] items-center mobileToDesk:flex-col"
							>
								<motion.div className="w-[50%] relative mobileToDesk:w-full">
									<p
										dangerouslySetInnerHTML={{
											__html: cardDetails?.summary?.processed || '',
										}}
										className="text-[16px] text-[#fff] font-poppins mb-[40px] mobileMax:mb-6 leading-8 mobileMax:text-xsmall mobileMax:leading-normal bold-font-description about"
									/>
								</motion.div>
								<div className="relative group desktop:ml-auto mobileToDesk:mx-auto mobileToDesk:my-5 w-[100%] max-w-[600px] aspect-[550/515] mobileMax:w-full mobileMax:max-w-[320px] rounded-[40px]">
									<Image
										alt="Background pattern"
										src="/static/images/unified-about.webp"
										fill
										style={{ objectPosition: 'center 50%' }}
										className="object-contain"
									/>
								</div>
							</motion.div>
						</div>
					</div>
				)}

				{(cardDetails?.description?.value ||
					cardDetails?.description?.processed) && (
					<div className="mini-container py-[120px]">
						<div
							className="--font-poppins news-article text-left text-[16px] text-[#003350] leading-7 mb-5 mobileMax:text-xsmall mobileMax:leading-normal"
							dangerouslySetInnerHTML={{
								__html:
									cardDetails?.description?.value ||
									cardDetails?.description?.processed ||
									'',
							}}
						/>

						{(() => {
							const validRelatedContent =
								cardDetails?.field_related_content?.filter(
									(newsData: DrupalNode) =>
										(newsData?.field_title || newsData?.title) &&
										(newsData?.field_link?.uri || newsData?.field_link),
								) || [];

							if (validRelatedContent.length === 0) return null;

							return (
								<div className="mini-container py-[120px] mobileMax:py-16">
									{/* <h2 className="text-[32px] mobileMax:text-[24px] font-bold text-[#003350] mb-12 mobileMax:mb-8 --font-poppins">
                    Related Content
                  </h2> */}
									<div className="grid grid-cols-3 gap-6 mobileMax:grid-cols-1 betweenMobileTab:grid-cols-2">
										{validRelatedContent.map(
											(newsData: DrupalNode, index: number) => {
												const linkData =
													newsData.field_link?.uri ||
													newsData.field_link ||
													'#';
												const redirectLink = resolveLink(linkData);

												const imageUrl = newsData?.field_thumbnail?.uri?.url
													? `${config.apiBase}${newsData.field_thumbnail.uri.url}`
													: newsData?.field_image?.uri?.url
														? `${config.apiBase}${newsData.field_image.uri.url}`
														: newsData?.field_news_media_url;

												return (
													<Link
														href={redirectLink}
														key={index}
														className="relative w-full h-[420px] rounded-[30px] overflow-hidden shadow-md group duration-700 hover:scale-[1.02] hover:shadow-2xl transition-all"
														target={'_blank'}
													>
														{/* Background Image or Video Thumbnail */}
														{redirectLink?.includes('youtube.com') ||
														redirectLink?.includes('youtu.be') ? (
															<div className="absolute inset-0 w-full h-full">
																<ReactPlayer
																	url={redirectLink}
																	width="100%"
																	height="100%"
																	light={
																		<div className="w-full h-full relative">
																			<img
																				src={`https://img.youtube.com/vi/${(() => {
																					const regExp =
																						/^.*(youtu\.be\/|v\/|u\/\w\/|embed\/|watch\?v=|\&v=)([^#\&\?]*).*/;
																					const match =
																						redirectLink.match(regExp);
																					return match && match[2].length === 11
																						? match[2]
																						: redirectLink.split('/').pop();
																				})()}/maxresdefault.jpg`}
																				onError={(e) => {
																					const target =
																						e.target as HTMLImageElement;
																					if (
																						!target.src.includes(
																							'hqdefault.jpg',
																						)
																					) {
																						target.src = target.src.replace(
																							'maxresdefault.jpg',
																							'hqdefault.jpg',
																						);
																						target.classList.add(
																							'scale-[1.15]',
																						); // Zoom in to hide baked-in bars
																					}
																				}}
																				alt="video thumbnail"
																				className="w-full h-full object-cover transition-transform duration-500"
																			/>
																		</div>
																	}
																/>
															</div>
														) : imageUrl ? (
															<div className="absolute inset-0">
																<Image
																	src={imageUrl}
																	alt={newsData?.title || 'news image'}
																	fill
																	className="object-cover"
																	sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
																/>
															</div>
														) : (
															<div className="absolute inset-0 bg-[#003350]" />
														)}

														{/* Overlay gradient */}
														<div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/20 to-transparent" />

														{/* Content */}
														<div className="absolute bottom-6 left-6 right-6 text-white">
															<h3 className="text-[20px] font-semibold mb-2 leading-tight line-clamp-2">
																{newsData?.field_title || newsData?.title}
															</h3>

															<div className="inline-flex items-center text-[16px] font-medium text-white opacity-90">
																Learn more
																<MdChevronRight className="w-5 h-5 ml-1" />
															</div>
														</div>
													</Link>
												);
											},
										)}
									</div>
								</div>
							);
						})()}
					</div>
				)}

				{isRegister && (
					<div className="bg-[linear-gradient(to_top, #003350 0%, #3771A1 100%)] py-[120px] betweenMobileTab:py-16 mobileMax:py-10 relative ">
						<div
							id="register"
							className="mini-container flex flex-col items-center justify-center"
						>
							<div className="bg-white mobileMax:p-6 py-14 px-14 rounded-[20px] z-[1]">
								<p className="text-[32px] bg-clip-text bg-[linear-gradient(to_top,#8EBCE5,#11375C)] text-clip font-semibold text-center">
									Register here
								</p>
								<RegisterForm />
							</div>
						</div>
					</div>
				)}
			</div>
			<RegisterSuccessModal
				open={showSuccessModal}
				setOpen={setShowSuccessModal}
			/>
		</>
	);
};

export default DetailScreen;
